import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronLeft, Calculator, TrendingUp, AlertCircle, Search, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function IntrinsicValueCalculator({ user, onLogout }) {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('Reliance Industries');
  const [currentFCF, setCurrentFCF] = useState(50000); // in crores
  const [growthRate, setGrowthRate] = useState(15); // %
  const [discountRate, setDiscountRate] = useState(12); // %
  const [terminalGrowth, setTerminalGrowth] = useState(4); // %
  const [projectionYears, setProjectionYears] = useState(5);
  const [totalShares, setTotalShares] = useState(675); // in crores
  
  // Company search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [dataSource, setDataSource] = useState('Manual Input');
  const [currentMarketPrice, setCurrentMarketPrice] = useState(0);
  
  // Clear selection and reset
  const clearSelection = () => {
    setSelectedSymbol('');
    setCompanyName('');
    setSearchQuery('');
    setLastUpdated('');
    setDataSource('Manual Input');
    setCurrentMarketPrice(0);
    searchCompanies(''); // Reload all companies
  };
  
  // Search companies
  const searchCompanies = async (query = '') => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/companies/search?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      setSearchResults(data.companies || []);
    } catch (error) {
      console.error('Error searching companies:', error);
      setSearchResults([]);
    }
  };
  
  // Fetch company data
  const fetchCompanyData = async (symbol) => {
    setIsLoadingData(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/company-data/${symbol}`);
      const data = await response.json();
      
      if (data.success) {
        // Populate form with live data
        setCompanyName(data.company_name);
        setCurrentFCF(data.data.free_cash_flow || 50000);
        setGrowthRate(data.data.estimated_growth_rate || 12);
        setTotalShares(data.data.shares_outstanding || 100);
        setCurrentMarketPrice(data.data.current_price || 0);
        setSelectedSymbol(data.symbol);
        setLastUpdated(new Date(data.last_updated).toLocaleString());
        setDataSource(`${data.source}`);
        setSearchQuery(''); // Clear search to hide dropdown
        
        // Show success notification
        alert(`✅ Loaded verified data for ${data.company_name}\n\nData Source: ${data.source}`);
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
      alert(`❌ Could not load data. This company may not be in our verified database yet.\n\nPlease use manual entry.`);
    } finally {
      setIsLoadingData(false);
    }
  };
  
  // Handle company selection
  const handleCompanySelect = (company) => {
    setSearchQuery(company.display);
    setShowDropdown(false);
    fetchCompanyData(company.symbol);
  };
  
  // Load all companies on mount for easy discovery
  useEffect(() => {
    searchCompanies(''); // Load all companies initially
  }, []);
  
  // Handle search input change
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      searchCompanies(searchQuery);
    }, 300);
    
    return () => clearTimeout(delaySearch);
  }, [searchQuery]);
  
  // Calculate DCF Intrinsic Value
  const calculateDCF = () => {
    // Input validation
    if (currentFCF <= 0 || totalShares <= 0) {
      return {
        error: 'Current FCF and Total Shares must be positive numbers',
        projectedFCFs: [],
        totalPV: 0,
        terminalValue: 0,
        terminalValuePV: 0,
        enterpriseValue: 0,
        intrinsicValuePerShare: 0
      };
    }
    
    if (discountRate <= terminalGrowth) {
      return {
        error: 'Discount Rate must be greater than Terminal Growth Rate for valid valuation',
        projectedFCFs: [],
        totalPV: 0,
        terminalValue: 0,
        terminalValuePV: 0,
        enterpriseValue: 0,
        intrinsicValuePerShare: 0
      };
    }
    
    if (discountRate <= 0 || discountRate > 100) {
      return {
        error: 'Discount Rate must be between 0% and 100%',
        projectedFCFs: [],
        totalPV: 0,
        terminalValue: 0,
        terminalValuePV: 0,
        enterpriseValue: 0,
        intrinsicValuePerShare: 0
      };
    }
    
    const projectedFCFs = [];
    let fcf = currentFCF;
    let totalPV = 0;
    
    // Project FCF for specified years
    for (let i = 1; i <= projectionYears; i++) {
      fcf = fcf * (1 + growthRate / 100);
      const pv = fcf / Math.pow(1 + discountRate / 100, i);
      projectedFCFs.push({
        year: i,
        fcf: Math.round(fcf),
        pv: Math.round(pv)
      });
      totalPV += pv;
    }
    
    // Calculate Terminal Value using Gordon Growth Model
    const terminalFCF = fcf * (1 + terminalGrowth / 100);
    const terminalValue = terminalFCF / ((discountRate - terminalGrowth) / 100);
    const terminalValuePV = terminalValue / Math.pow(1 + discountRate / 100, projectionYears);
    
    // Total Enterprise Value
    const enterpriseValue = totalPV + terminalValuePV;
    
    // Intrinsic Value per Share
    const intrinsicValuePerShare = enterpriseValue / totalShares;
    
    return {
      error: null,
      projectedFCFs,
      totalPV: Math.round(totalPV),
      terminalValue: Math.round(terminalValue),
      terminalValuePV: Math.round(terminalValuePV),
      enterpriseValue: Math.round(enterpriseValue),
      intrinsicValuePerShare: Math.round(intrinsicValuePerShare)
    };
  };

  const result = calculateDCF();

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto">
        
        <Button 
          variant="ghost" 
          onClick={() => navigate('/learn')} 
          className="mb-6 dark:text-gray-300"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Learn Home
        </Button>

        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100">Intrinsic Value Calculator (DCF)</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 ml-15">
            Calculate the true worth of a stock using Discounted Cash Flow (DCF) method
          </p>
        </div>

        {/* Compact Disclaimer */}
        <Card className="glass p-3 mb-6 dark:bg-gray-800 border-l-4 border-blue-500 bg-blue-50/30 dark:bg-blue-900/10">
          <div className="text-xs text-gray-600 dark:text-gray-400">
            <span className="font-semibold text-gray-700 dark:text-gray-300">Educational Tool:</span> This DCF calculator is for learning purposes. Valuations depend on your assumptions. Always use conservative estimates and consult financial advisors before investing.
          </div>
        </Card>

        {/* Company Search */}
        <Card className="glass p-4 mb-6 dark:bg-gray-800 border-l-4 border-green-500">
          <div className="flex items-start space-x-3">
            <Search className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold text-gray-800 dark:text-gray-100">
                  🚀 Quick Start: Select a Company with Verified Data
                </p>
                {selectedSymbol && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={clearSelection}
                    className="text-xs"
                  >
                    ← Change Company
                  </Button>
                )}
              </div>
              
              {/* Visible Company Chips */}
              {!selectedSymbol && (
              <div className="mb-3">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 font-semibold">
                  Click any company to auto-load financial data:
                </p>
                <div className="flex flex-wrap gap-2">
                  {searchResults.slice(0, 10).map((company) => (
                    <button
                      key={company.symbol}
                      onClick={() => handleCompanySelect(company)}
                      disabled={isLoadingData}
                      className="px-3 py-1.5 text-sm bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-full font-medium transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {company.name}
                    </button>
                  ))}
                </div>
              </div>
              )}
              
              {/* Current Selection Display */}
              {selectedSymbol && (
                <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                  <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                    ✅ Selected: {companyName}
                  </p>
                  {currentMarketPrice > 0 && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Current Market Price: ₹{currentMarketPrice.toLocaleString('en-IN')}
                    </p>
                  )}
                </div>
              )}
              
              {/* Search box for filtering (optional) */}
              {!selectedSymbol && (
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Or type to search/filter companies..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  className="dark:bg-gray-700 dark:text-gray-100 text-sm"
                  disabled={isLoadingData}
                />
              </div>
              )}
              
              {lastUpdated && (
                <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
                  <span className="font-semibold">Data Source:</span> {dataSource} | 
                  <span className="font-semibold ml-2">Last Updated:</span> {lastUpdated}
                </div>
              )}
              
              {isLoadingData && (
                <div className="mt-2 flex items-center text-sm text-blue-600 dark:text-blue-400">
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Loading verified data...
                </div>
              )}
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Inputs */}
          <div className="lg:col-span-1">
            <Card className="glass p-6 dark:bg-gray-800">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Input Parameters</h2>
                {selectedSymbol && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fetchCompanyData(selectedSymbol)}
                    className="text-xs"
                    disabled={isLoadingData}
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Refresh
                  </Button>
                )}
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Company Name
                  </label>
                  <Input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Current Free Cash Flow (₹ Crores)
                  </label>
                  <Input
                    type="number"
                    value={currentFCF}
                    onChange={(e) => setCurrentFCF(Number(e.target.value))}
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Find this in annual reports or screener.in
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Expected Growth Rate (%)
                  </label>
                  <Input
                    type="number"
                    value={growthRate}
                    onChange={(e) => setGrowthRate(Number(e.target.value))}
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Historical avg: 10-20% for good companies
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Discount Rate/WACC (%)
                  </label>
                  <Input
                    type="number"
                    value={discountRate}
                    onChange={(e) => setDiscountRate(Number(e.target.value))}
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Typical: 10-15% (your required return)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Terminal Growth Rate (%)
                  </label>
                  <Input
                    type="number"
                    value={terminalGrowth}
                    onChange={(e) => setTerminalGrowth(Number(e.target.value))}
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Conservative: 3-5% (GDP growth rate)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Total Outstanding Shares (Crores)
                  </label>
                  <Input
                    type="number"
                    value={totalShares}
                    onChange={(e) => setTotalShares(Number(e.target.value))}
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Available on NSE/BSE or screener.in
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Results */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Error Display - Only show when not loading */}
            {result.error && !isLoadingData && (
              <Card className="glass p-6 dark:bg-gray-800 border-2 border-red-500">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-red-600 dark:text-red-400 mb-2">Validation Error</h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{result.error}</p>
                  </div>
                </div>
              </Card>
            )}
            
            {/* Final Intrinsic Value */}
            {!result.error && (
              <Card className="glass p-6 dark:bg-gray-800 border-2 border-green-500">
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">Intrinsic Value Result</h2>
                <div className="text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{companyName}</p>
                  <p className="text-5xl font-bold text-green-600 dark:text-green-400 mb-2">
                    ₹{result.intrinsicValuePerShare.toLocaleString('en-IN')}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Intrinsic Value per share</p>
                  
                  {/* Market Price Comparison */}
                  {currentMarketPrice > 0 && (
                    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Current Market Price</p>
                          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            ₹{currentMarketPrice.toLocaleString('en-IN')}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Difference</p>
                          <p className={`text-2xl font-bold ${
                            result.intrinsicValuePerShare > currentMarketPrice ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                          }`}>
                            {((result.intrinsicValuePerShare - currentMarketPrice) / currentMarketPrice * 100).toFixed(1)}%
                          </p>
                        </div>
                      </div>
                      <p className="text-xs font-semibold">
                        {result.intrinsicValuePerShare > currentMarketPrice ? (
                          <span className="text-green-600 dark:text-green-400">
                            ✅ Stock appears UNDERVALUED (Intrinsic &gt; Market)
                          </span>
                        ) : (
                          <span className="text-red-600 dark:text-red-400">
                            ⚠️ Stock appears OVERVALUED (Intrinsic &lt; Market)
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                  
                  <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-xs text-gray-700 dark:text-gray-300">
                      <strong>💡 Investment Decision:</strong> Apply a margin of safety (buy at 20-30% below intrinsic value) before investing. This is not financial advice.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* DCF Breakdown */}
            {!result.error && (
              <Card className="glass p-6 dark:bg-gray-800">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">DCF Calculation Breakdown</h3>
              
              <div className="space-y-4">
                {/* Projected Cash Flows */}
                <div>
                  <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2 text-sm">Projected Free Cash Flows ({projectionYears} Years)</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b dark:border-gray-700">
                          <th className="text-left py-2 text-gray-700 dark:text-gray-300">Year</th>
                          <th className="text-right py-2 text-gray-700 dark:text-gray-300">FCF (₹ Cr)</th>
                          <th className="text-right py-2 text-gray-700 dark:text-gray-300">Present Value (₹ Cr)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.projectedFCFs.map((item) => (
                          <tr key={item.year} className="border-b dark:border-gray-700">
                            <td className="py-2 text-gray-600 dark:text-gray-400">Year {item.year}</td>
                            <td className="text-right py-2 text-gray-600 dark:text-gray-400">{item.fcf.toLocaleString('en-IN')}</td>
                            <td className="text-right py-2 text-gray-600 dark:text-gray-400">{item.pv.toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                    <p className="text-xs text-gray-600 dark:text-gray-400">PV of Projected FCFs</p>
                    <p className="text-lg font-bold text-gray-800 dark:text-gray-100">₹{result.totalPV.toLocaleString('en-IN')} Cr</p>
                  </div>
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded">
                    <p className="text-xs text-gray-600 dark:text-gray-400">PV of Terminal Value</p>
                    <p className="text-lg font-bold text-gray-800 dark:text-gray-100">₹{result.terminalValuePV.toLocaleString('en-IN')} Cr</p>
                  </div>
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded col-span-2">
                    <p className="text-xs text-gray-600 dark:text-gray-400">Total Enterprise Value</p>
                    <p className="text-xl font-bold text-gray-800 dark:text-gray-100">₹{result.enterpriseValue.toLocaleString('en-IN')} Crores</p>
                  </div>
                </div>
              </div>
            </Card>
            )}

            {/* Understanding DCF */}
            <Card className="glass p-6 dark:bg-gray-800">
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">Understanding the DCF Method</h3>
              <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                <div>
                  <p className="font-semibold text-gray-800 dark:text-gray-100 mb-1">Step 1: Project Future Cash Flows</p>
                  <p>Estimate how much free cash the company will generate over the next {projectionYears} years based on historical growth.</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 dark:text-gray-100 mb-1">Step 2: Calculate Terminal Value</p>
                  <p>Estimate the company's value beyond year {projectionYears} assuming steady growth at terminal rate.</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 dark:text-gray-100 mb-1">Step 3: Discount to Present Value</p>
                  <p>Apply discount rate (your required return) to adjust future cash flows to today's value.</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 dark:text-gray-100 mb-1">Step 4: Calculate Per Share Value</p>
                  <p>Divide total enterprise value by outstanding shares to get intrinsic value per share.</p>
                </div>
              </div>
            </Card>

          </div>
        </div>

      </div>
      <Footer />
    </div>
  );
}
